"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";

const Header3 = ({ data }: { data: any }) => {
  const { data: sectionData, isPreview = false } = data;

  if (!sectionData) {
    return null;
  }

  const {
    promotional_text,
    promotional_text_color,
    gradient_color_1,
    gradient_color_2,
    phone_number,
    phone_icon,
    phone_icon_alt,
    phone_number_text_color,
    button_name,
    button_icon,
    button_icon_alt,
    button_link,
    button_color,
    button_text_color,
  } = sectionData;

  return (
    <div
      className={cn(
        "flex items-center justify-center w-full",
        "gap-[12px] px-[24px] py-[12px]",
        "sm:px-[40px] sm:py-[8px]",
        "md:px-[50px] md:py-[12px]",
        "lg:px-[60px]",
        "xl:px-[120px]",
        isPreview ? "relative" : "sticky top-0 z-50",
      )}
      style={{
        background: `linear-gradient(to right, ${gradient_color_1 || "#342b7c"}, ${gradient_color_2 || "#00d4eb"})`,
      }}
    >
      {/* Announcement Text */}
      {promotional_text && (
        <>
          <style>{`
            @keyframes header3-marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `}</style>
          <div className="flex-1 min-w-0 overflow-hidden">
            {/* Mobile: auto-scrolling marquee */}
            <div
              className={cn(
                "flex items-center whitespace-nowrap w-max",
                "sm:hidden",
              )}
              style={{
                animation: "header3-marquee 18s linear infinite",
                color: promotional_text_color || "#ffffff",
              }}
            >
              <span className="inline-block shrink-0 text-[11px] font-semibold leading-normal pr-[60px]">
                {promotional_text}
              </span>
              <span className="inline-block shrink-0 text-[11px] font-semibold leading-normal pr-[60px]">
                {promotional_text}
              </span>
            </div>
            {/* Large screen: truncate */}
            <p
              className={cn(
                "hidden sm:block truncate text-[11px] font-semibold leading-normal",
                "sm:tracking-[0.11px]",
                "md:text-[12px] md:tracking-[0.12px]",
                "lg:text-[14px] lg:tracking-[0.14px]",
              )}
              style={{ color: promotional_text_color || "#ffffff" }}
              title={promotional_text}
            >
              {promotional_text}
            </p>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex items-start gap-[12px] sm:gap-[10px]">
        {/* Phone Button */}
        {phone_number && (
          <Link
            href={`tel:${phone_number}`}
            className={cn(
              "flex items-center justify-center shrink-0",
              "size-[40px] rounded-[26px]",
              "sm:size-auto sm:gap-[6px] sm:px-[12px] sm:py-[8px]",
              "md:py-[10px] md:h-[40px]",
            )}
            style={{
              backgroundColor: "rgba(255,255,255,0.1)",
            }}
          >
            {phone_icon && (
              <div className="relative shrink-0 size-[18px] sm:size-[20px]">
                <Image
                  src={phone_icon}
                  alt={phone_icon_alt || "Phone"}
                  fill
                  className="object-contain"
                />
              </div>
            )}
            <span
              className="hidden sm:inline sm:text-[12px] sm:font-semibold sm:leading-normal shrink-0"
              style={{ color: phone_number_text_color || "#ffffff" }}
            >
              {phone_number}
            </span>
          </Link>
        )}

        {/* WhatsApp / Action Button */}
        {button_link && (
          <Link
            href={button_link}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center justify-center shrink-0 overflow-hidden",
              "size-[40px] rounded-[24px]",
              "sm:size-auto sm:gap-[8px] sm:px-[12px] sm:py-[8px]",
              "md:py-[10px] md:h-[40px]",
              "shadow-[0px_2px_6px_0px_rgba(9,30,66,0.04),0px_8px_12px_0px_rgba(9,30,66,0.08)]",
            )}
            style={{
              backgroundColor: button_color || "#4caf51",
            }}
          >
            {button_icon && (
              <div className="relative shrink-0 size-[18px] sm:size-[20px]">
                <Image
                  src={button_icon}
                  alt={button_icon_alt || "WhatsApp"}
                  fill
                  className="object-contain"
                />
              </div>
            )}
            <span
              className="hidden sm:inline sm:text-[12px] sm:font-semibold sm:leading-normal shrink-0"
              style={{ color: button_text_color || "#ffffff" }}
            >
              {button_name || "WhatsApp Chat"}
            </span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default Header3;
